import { apiClient } from './api';
import { authEvents } from './auth-events';
import { AuthTokenService } from './auth-token-service';
import { authLogger } from './logger';

/**
 * Acquisition attribution: ties the ad click or campaign that brought a visitor
 * to the account created afterwards, so purchases can later be reported back
 * to the ad platforms (server-side conversions) and channels can be compared.
 *
 * Sources, in order of precedence (first touch wins):
 *   1. a touch already pending in localStorage from an earlier boot;
 *   2. the landing's first-touch cookie `gf_attr` (set on the shared parent
 *      domain `.getfacade.ai` / `.getfacade.ru`, see apps/landing/lib/attribution.ts);
 *   3. the app URL itself, for ads and links that point straight at the app.
 *
 * The touch waits in localStorage (`gf_attribution_pending`) because the visit
 * that carries it is usually anonymous, and sign-in (email, Google, Apple) can
 * leave the page for an OAuth round trip. Every sign-in path stores its token
 * through AuthTokenService, which emits `token:updated`; that is the one
 * chokepoint this module listens to, so a new auth path cannot forget to send.
 *
 * Once the API has accepted the touch it is dropped and `gf_attribution_sent`
 * stops the same cookie from being queued again on later boots. Logout clears
 * that marker, so a different account signing in on this browser still sends
 * (the API only records a touch for accounts younger than 30 days and never
 * overwrites a field it already has, so a repeat is harmless).
 */

export const PENDING_KEY = 'gf_attribution_pending';
export const SENT_KEY = 'gf_attribution_sent';
export const LANDING_COOKIE = 'gf_attr';
const ENDPOINT = '/v1/me/attribution';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'yclid', 'msclkid'] as const;
const MAX_VALUE_LENGTH = 512;

/** Wire shape of `POST /api/v1/me/attribution` (all fields optional). */
export interface AttributionPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ttclid?: string;
  yclid?: string;
  msclkid?: string;
  referrer?: string;
  landing_path?: string;
  touched_at?: string;
  ads_consent?: boolean;
}

const STRING_KEYS: readonly (keyof AttributionPayload)[] = [
  ...UTM_KEYS,
  ...CLICK_ID_KEYS,
  'fbc',
  'fbp',
  'referrer',
  'landing_path',
  'touched_at',
];

function clean(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, MAX_VALUE_LENGTH) : undefined;
}

/** Keeps only known keys with well-typed values; anything else is dropped. */
export function sanitize(raw: unknown): AttributionPayload | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const result: AttributionPayload = {};
  for (const key of STRING_KEYS) {
    const value = clean(source[key]);
    if (value !== undefined) (result as Record<string, unknown>)[key] = value;
  }
  if (typeof source.ads_consent === 'boolean') result.ads_consent = source.ads_consent;
  return Object.keys(result).length > 0 ? result : null;
}

/** Registrable parent of a host (`app.getfacade.ai` → `getfacade.ai`); null for IPs and localhost. */
function parentDomain(host: string): string | null {
  if (!host || host === 'localhost' || /^[\d.]+$/.test(host) || host.includes(':')) return null;
  const labels = host.split('.');
  return labels.length >= 2 ? labels.slice(-2).join('.') : null;
}

/** Host of an external referrer; our own hosts (landing, app) are not a source. */
function externalReferrerHost(referrer: string, currentHost: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    const ownParent = parentDomain(currentHost);
    if (!host || host === currentHost) return undefined;
    if (ownParent && (host === ownParent || host.endsWith(`.${ownParent}`))) return undefined;
    return host;
  } catch {
    return undefined;
  }
}

/**
 * Touch of an app page opened directly from an ad or a link. Returns null when
 * the URL carries no utm, click id or external referrer.
 */
export function parseUrlTouch(input: {
  search: string;
  pathname: string;
  referrer: string;
  host: string;
  now?: Date;
}): AttributionPayload | null {
  const params = new URLSearchParams(input.search);
  const touch: Record<string, string | undefined> = {};
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) touch[key] = clean(params.get(key));
  touch.referrer = externalReferrerHost(input.referrer, input.host.toLowerCase());
  if (!Object.values(touch).some((value) => value !== undefined)) return null;

  touch.landing_path = clean(input.pathname);
  touch.touched_at = (input.now ?? new Date()).toISOString();
  return sanitize(touch);
}

export function decodeLandingCookie(raw: string | null | undefined): AttributionPayload | null {
  if (!raw) return null;
  try {
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    return sanitize(JSON.parse(new TextDecoder().decode(bytes)));
  } catch {
    return null;
  }
}

/**
 * First touch wins. The landing cookie only refreshes `ads_consent` on a touch
 * that came from the same cookie earlier, since the visitor may answer the
 * cookie banner after the touch was recorded.
 */
export function resolvePending(
  pending: AttributionPayload | null,
  landing: AttributionPayload | null,
  url: AttributionPayload | null
): AttributionPayload | null {
  if (pending) {
    if (landing && landing.touched_at === pending.touched_at && landing.ads_consent !== undefined) {
      return { ...pending, ads_consent: landing.ads_consent };
    }
    return pending;
  }
  return landing ?? url;
}

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
}

function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage disabled (private mode, quota): attribution is best-effort.
  }
}

function readPending(): AttributionPayload | null {
  const raw = storageGet(PENDING_KEY);
  if (!raw) return null;
  try {
    return sanitize(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Reads the landing cookie and the current URL into the pending slot. */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  if (storageGet(SENT_KEY)) return;

  const resolved = resolvePending(
    readPending(),
    decodeLandingCookie(readCookie(LANDING_COOKIE)),
    parseUrlTouch({
      search: window.location.search,
      pathname: window.location.pathname,
      referrer: document.referrer,
      host: window.location.hostname,
    })
  );
  if (resolved) storageSet(PENDING_KEY, JSON.stringify(resolved));
}

let inFlight = false;

/** Sends the pending touch once a session exists. Safe to call repeatedly. */
export async function flushAttribution(): Promise<void> {
  if (typeof window === 'undefined' || inFlight) return;
  if (!AuthTokenService.getTokenSync()) return;
  const pending = readPending();
  if (!pending) return;

  inFlight = true;
  try {
    await apiClient.post<void>(ENDPOINT, pending, { expectedStatuses: [403] });
    storageSet(PENDING_KEY, null);
    storageSet(SENT_KEY, '1');
  } catch (error) {
    // Kept for the next sign-in or boot; e.g. an unverified account is
    // refused until the email is confirmed.
    authLogger.debug('Attribution not sent yet:', error);
  } finally {
    inFlight = false;
  }
}

let started = false;

/** Boot hook: capture now, send now if signed in, and after every sign-in. */
export function startAttributionSync(): void {
  if (typeof window === 'undefined' || started) return;
  started = true;

  captureAttribution();
  authEvents.on('token:updated', (event) => {
    if (event.payload) {
      // Deferred so ApiClient, which listens to the same event, has the new
      // token before this request goes out.
      setTimeout(() => void flushAttribution(), 0);
    } else {
      storageSet(SENT_KEY, null);
    }
  });
  void flushAttribution();
}
